import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { TERMS } from '@/data/legalTerms';
import { PRIVACY } from '@/data/legalPrivacy';
import { PublicFooter } from '@/components/PublicFooter';
import { PublicHeader } from '@/components/PublicHeader';
import { MarkupText } from '@/components/MarkupText';
import { PUBLIC_CONTAINER } from '@/components/PublicContainer';

const API = process.env.REACT_APP_BACKEND_URL;

// Static structured fallback renderer (used only if the DB record is unexpectedly missing).
function renderText(text, crossPhrase, crossTo) {
  const EMAIL_RE = /(hello@sergeanttap\.com)/g;
  let nodes = [text];
  if (crossPhrase) {
    const out = [];
    nodes.forEach((seg) => {
      if (typeof seg !== 'string') { out.push(seg); return; }
      const parts = seg.split(crossPhrase);
      parts.forEach((p, i) => {
        if (p) out.push(p);
        if (i < parts.length - 1) out.push(<Link key={`x${out.length}`} to={crossTo} className="text-brand underline hover:no-underline">{crossPhrase}</Link>);
      });
    });
    nodes = out;
  }
  const final = [];
  nodes.forEach((seg) => {
    if (typeof seg !== 'string') { final.push(seg); return; }
    const parts = seg.split(EMAIL_RE);
    parts.forEach((p, i) => {
      if (!p) return;
      if (i % 2 === 1) final.push(<a key={`m${final.length}`} href={`mailto:${p}`} className="text-brand underline hover:no-underline">{p}</a>);
      else final.push(p);
    });
  });
  return final;
}
function Block({ b, crossPhrase, crossTo }) {
  if (b.t === 'h2') return <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mt-10 mb-3">{b.x}</h2>;
  if (b.t === 'h3') return <h3 className="text-base sm:text-lg font-semibold text-slate-800 mt-6 mb-2">{b.x}</h3>;
  if (b.t === 'caps') return <p className="text-[13px] sm:text-sm font-semibold tracking-wide text-slate-800 my-3 leading-relaxed">{renderText(b.x, crossPhrase, crossTo)}</p>;
  if (b.t === 'ul') return <ul className="list-disc pl-6 my-3 space-y-1.5 text-[15px] text-slate-700 leading-relaxed">{b.x.map((li, i) => <li key={i}>{renderText(li, crossPhrase, crossTo)}</li>)}</ul>;
  return <p className="text-[15px] text-slate-700 leading-relaxed my-3">{renderText(b.x, crossPhrase, crossTo)}</p>;
}

function LegalDocument({ contentKey, fallback, crossPhrase, crossTo }) {
  const [doc, setDoc] = useState(undefined); // undefined=loading

  useEffect(() => {
    window.scrollTo(0, 0);
    axios.get(`${API}/api/public/site-content/${contentKey}`)
      .then((r) => { setDoc(r.data); document.title = `${r.data.title} · Sergeant TAP`; })
      .catch(() => { setDoc(null); document.title = `${fallback.title} · Sergeant TAP`; });
  }, [contentKey, fallback.title]);

  const subNavCls = (active) => `text-sm font-semibold transition-colors ${active ? 'text-brand' : 'text-slate-500 hover:text-brand'}`;

  return (
    <div className="min-h-screen bg-[#fafafa] flex flex-col" data-testid="legal-page">
      <PublicHeader />

      {/* Secondary legal sub-navigation — primary nav stays in PublicHeader above. */}
      <div className="bg-white border-b border-[#eee]" data-testid="legal-subnav">
        <div className={`${PUBLIC_CONTAINER} h-11 flex items-center gap-5`}>
          <Link to="/terms" className={subNavCls(contentKey === 'terms')}>Terms</Link>
          <Link to="/privacy" className={subNavCls(contentKey === 'privacy')}>Privacy</Link>
        </div>
      </div>

      <main className="flex-1">
        {doc === undefined ? (
          <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand" /></div>
        ) : doc ? (
          <article className={`${PUBLIC_CONTAINER} py-10 sm:py-14`} data-testid="legal-content">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="legal-title">{doc.title}</h1>
            {(doc.effective_date || doc.last_updated) && (
              <div className="mt-3 text-sm text-slate-500 space-y-0.5">
                {doc.effective_date && <p data-testid="legal-effective-date">Effective Date: {doc.effective_date}</p>}
                {doc.last_updated && <p data-testid="legal-last-updated">Last Updated: {doc.last_updated}</p>}
              </div>
            )}
            <div className="mt-6 border-t border-slate-200 pt-6 max-w-3xl">
              <MarkupText text={doc.body} />
            </div>
          </article>
        ) : (
          // Emergency static fallback — preserves original wording if DB record is missing.
          <article className={`${PUBLIC_CONTAINER} py-10 sm:py-14`} data-testid="legal-content">
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900" data-testid="legal-title">{fallback.title}</h1>
            <div className="mt-3 text-sm text-slate-500 space-y-0.5">
              <p data-testid="legal-effective-date">Effective Date: {fallback.effectiveDate}</p>
              <p data-testid="legal-last-updated">Last Updated: {fallback.lastUpdated}</p>
            </div>
            <div className="mt-6 border-t border-slate-200 pt-6 max-w-3xl">
              {fallback.intro.map((b, i) => <Block key={`intro-${i}`} b={b} crossPhrase={crossPhrase} crossTo={crossTo} />)}
              {fallback.blocks.map((b, i) => <Block key={i} b={b} crossPhrase={crossPhrase} crossTo={crossTo} />)}
            </div>
          </article>
        )}
      </main>

      <PublicFooter />
    </div>
  );
}

export const TermsPage = () => <LegalDocument contentKey="terms" fallback={TERMS} crossPhrase="Privacy Policy" crossTo="/privacy" />;
export const PrivacyPage = () => <LegalDocument contentKey="privacy" fallback={PRIVACY} crossPhrase="Terms of Service" crossTo="/terms" />;
